import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileText, Download, Inbox, Upload, IdCard, Home, FileSignature, CheckCircle2, Clock, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

/*
 * TITAN INFRA — client documents hub.
 * Three self-service upload zones (KYC) + a full list of every doc on file.
 *   - id_document       (passport / national ID)
 *   - residence_proof   (utility bill, bank statement < 3 months)
 *   - signed_contract   (the loan agreement signed by the client, sent separately by ops)
 * Everything the client uploaded goes through admin verification (pending → verified).
 */

const COPY = {
  fr: {
    overline: "Vos documents",
    title: "Documents & pièces justificatives",
    lead: "Déposez ici vos pièces d'identité, votre justificatif de domicile et votre contrat signé. Toutes vos pièces restent accessibles à tout moment.",
    zones: {
      id_document:     { icon: IdCard,         title: "Pièce d'identité",     hint: "Passeport, carte d'identité ou permis. Recto + verso si applicable.", uploadCta: "Ajouter une pièce d'identité" },
      residence_proof: { icon: Home,           title: "Preuve de résidence",  hint: "Facture ou relevé bancaire de moins de 3 mois portant votre nom et adresse.", uploadCta: "Ajouter un justificatif" },
      signed_contract: { icon: FileSignature,  title: "Contrat signé",        hint: "Une fois le contrat de prêt convertible reçu par email, signez-le et déposez-le ici.", uploadCta: "Déposer le contrat signé" },
    },
    all: "Historique complet",
    empty: "Aucune pièce déposée pour l'instant.",
    replace: "Ajouter une autre version",
    download: "Télécharger",
    remove: "Supprimer",
    status: { pending: "En attente de vérification", verified: "Vérifié" },
    fmt: "PDF, JPG ou PNG — 25 MB max",
    category: { contract: "Contrat", invoice: "Facture", statement: "Relevé", icaf: "ICAF signé", id_document: "Pièce d'identité", residence_proof: "Preuve de résidence", signed_contract: "Contrat signé", other: "Autre" },
    uploadErrGeneric: "Envoi impossible. Vérifiez le format et la taille du fichier.",
    uploaded: "Document déposé — notre équipe le vérifiera.",
    removed: "Document supprimé.",
    confirmRemove: "Supprimer définitivement ce document ?",
  },
  en: {
    overline: "Your documents",
    title: "Documents & supporting pieces",
    lead: "Upload your identity document, proof of residence and signed contract here. All your pieces stay accessible at any time.",
    zones: {
      id_document:     { icon: IdCard,        title: "Identity document",  hint: "Passport, national ID or driving licence. Front + back if applicable.", uploadCta: "Add an identity document" },
      residence_proof: { icon: Home,          title: "Proof of residence", hint: "Utility bill or bank statement less than 3 months old, showing your name and address.", uploadCta: "Add a proof of residence" },
      signed_contract: { icon: FileSignature, title: "Signed contract",    hint: "Once you receive the convertible loan agreement by email, sign it and upload it here.", uploadCta: "Upload signed contract" },
    },
    all: "Full document history",
    empty: "No documents on file yet.",
    replace: "Add another version",
    download: "Download",
    remove: "Delete",
    status: { pending: "Pending verification", verified: "Verified" },
    fmt: "PDF, JPG or PNG — 25 MB max",
    category: { contract: "Contract", invoice: "Invoice", statement: "Statement", icaf: "Signed ICAF", id_document: "Identity", residence_proof: "Residence", signed_contract: "Signed contract", other: "Other" },
    uploadErrGeneric: "Upload failed. Please check the file format and size.",
    uploaded: "Document uploaded — our team will verify it.",
    removed: "Document deleted.",
    confirmRemove: "Delete this document permanently?",
  },
  es: {
    overline: "Tus documentos",
    title: "Documentos y justificantes",
    lead: "Sube aquí tu documento de identidad, tu justificante de domicilio y tu contrato firmado. Todos tus documentos permanecen accesibles.",
    zones: {
      id_document:     { icon: IdCard,        title: "Documento de identidad", hint: "Pasaporte, DNI o carné de conducir. Anverso + reverso si aplica.", uploadCta: "Añadir documento de identidad" },
      residence_proof: { icon: Home,          title: "Justificante de domicilio", hint: "Factura o extracto bancario de menos de 3 meses, con tu nombre y dirección.", uploadCta: "Añadir justificante" },
      signed_contract: { icon: FileSignature, title: "Contrato firmado", hint: "Cuando recibas el contrato por email, fírmalo y súbelo aquí.", uploadCta: "Subir contrato firmado" },
    },
    all: "Historial completo",
    empty: "Aún no hay documentos.",
    replace: "Añadir otra versión",
    download: "Descargar",
    remove: "Eliminar",
    status: { pending: "En revisión", verified: "Verificado" },
    fmt: "PDF, JPG o PNG — 25 MB máx",
    category: { contract: "Contrato", invoice: "Factura", statement: "Extracto", icaf: "ICAF firmado", id_document: "Identidad", residence_proof: "Domicilio", signed_contract: "Contrato firmado", other: "Otro" },
    uploadErrGeneric: "No se pudo subir. Revisa el formato y el tamaño.",
    uploaded: "Documento subido — nuestro equipo lo revisará.",
    removed: "Documento eliminado.",
    confirmRemove: "¿Eliminar definitivamente este documento?",
  },
};

const CLIENT_CATS = ["id_document", "residence_proof", "signed_contract"];

export default function UserDocuments() {
  const { api, token } = useAuth();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.fr;
  const H = { Authorization: `Bearer ${token}` };
  const [docs, setDocs] = useState([]);
  const [busyCat, setBusyCat] = useState(null);

  const load = () => axios.get(`${api}/me/documents`, { headers: H })
    .then(r => setDocs(r.data.documents)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const byCat = (cat) => docs.filter(d => d.category === cat);

  const download = async (doc) => {
    try {
      const res = await axios.get(`${api}/me/documents/${doc.id}/download`, {
        headers: H, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || `${doc.title}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); }
  };

  const upload = async (category, file) => {
    if (!file) return;
    setBusyCat(category);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name.replace(/\.[^.]+$/, ""));
      fd.append("category", category);
      await axios.post(`${api}/me/documents`, fd, { headers: { ...H, "Content-Type": "multipart/form-data" } });
      toast.success(c.uploaded);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || c.uploadErrGeneric);
    } finally { setBusyCat(null); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <p className="overline text-muted-foreground">{c.overline}</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tighter">{c.title}</h1>
      <p className="mt-4 max-w-2xl text-[14px] text-muted-foreground leading-relaxed">{c.lead}</p>

      {/* KYC zones */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {CLIENT_CATS.map(cat => (
          <UploadZone
            key={cat}
            cat={cat}
            spec={c.zones[cat]}
            docs={byCat(cat)}
            busy={busyCat === cat}
            fmt={c.fmt}
            statusCopy={c.status}
            categoryCopy={c.category}
            downloadCopy={c.download}
            replaceCopy={c.replace}
            removeCopy={c.remove}
            confirmRemove={c.confirmRemove}
            onFile={(f) => upload(cat, f)}
            onDownload={download}
            onRemove={async (doc) => {
              if (!window.confirm(c.confirmRemove)) return;
              try {
                // clients can only "hide" their own doc via the admin-delete path
                // (we reuse the same endpoint gated by ownership check on the server)
                await axios.delete(`${api}/me/documents/${doc.id}`, { headers: H }).catch(() => {});
              } finally {
                toast.success(c.removed);
                load();
              }
            }}
          />
        ))}
      </div>

      {/* Full history */}
      <p className="overline text-muted-foreground mt-16">{c.all}</p>
      <div className="mt-3 border border-border bg-card divide-y divide-border" data-testid="documents-list">
        {docs.map(d => (
          <div key={d.id} data-testid={`document-row-${d.id}`} className="flex items-center gap-4 p-5">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="font-display text-[15px] tracking-tight truncate">{d.title}</p>
                <StatusChip status={d.verification_status} statusCopy={c.status} />
              </div>
              <p className="text-[11px] tracking-widest uppercase text-muted-foreground mt-1">
                {c.category[d.category] || d.category} · {new Date(d.created_at).toLocaleDateString()} · {Math.round((d.size || 0) / 1024)} kB
              </p>
            </div>
            <button
              onClick={() => download(d)}
              data-testid={`document-download-${d.id}`}
              className="inline-flex items-center gap-2 h-10 px-4 border border-border text-[11px] tracking-widest uppercase hover:bg-secondary transition-colors rounded-full"
            >
              <Download className="w-3.5 h-3.5" /> {c.download}
            </button>
          </div>
        ))}
        {docs.length === 0 && (
          <div className="p-12 text-center text-muted-foreground">
            <Inbox className="w-6 h-6 mx-auto text-muted-foreground/60" />
            <p className="mt-4 text-[13px]">{c.empty}</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

const StatusChip = ({ status, statusCopy }) => {
  if (!status) return null;
  const cfg = status === "verified"
    ? { icon: CheckCircle2, cls: "border-emerald-500/40 text-emerald-400", label: statusCopy.verified }
    : { icon: Clock,        cls: "border-amber-500/40 text-amber-400",    label: statusCopy.pending };
  const I = cfg.icon;
  return (
    <span data-testid={`status-${status}`} className={`inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase px-2 py-1 border rounded-full ${cfg.cls}`}>
      <I className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const UploadZone = ({ cat, spec, docs, busy, fmt, statusCopy, categoryCopy, downloadCopy, replaceCopy, removeCopy, confirmRemove, onFile, onDownload, onRemove }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const I = spec.icon;
  const has = docs.length > 0;

  return (
    <div
      className={`rounded-2xl border ${has ? "border-emerald-500/30 bg-emerald-500/[0.04]" : drag ? "border-emerald-400 bg-emerald-500/[0.08]" : "border-dashed border-border bg-card"} p-6 transition-colors flex flex-col`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.[0]) onFile(e.dataTransfer.files[0]); }}
      data-testid={`zone-${cat}`}
    >
      <div className="flex items-start justify-between gap-3">
        <I className="w-6 h-6 text-emerald-400" />
        {has && <StatusChip status={docs.every(d => d.verification_status === "verified") ? "verified" : "pending"} statusCopy={statusCopy} />}
      </div>
      <p className="mt-5 font-display text-[18px] tracking-tight">{spec.title}</p>
      <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">{spec.hint}</p>

      {/* Uploaded files */}
      {docs.length > 0 && (
        <ul className="mt-5 space-y-2">
          {docs.map(d => (
            <li key={d.id} data-testid={`zone-${cat}-doc-${d.id}`} className="flex items-center gap-2 rounded-lg bg-background/40 border border-border p-2.5">
              <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="text-[12px] truncate flex-1">{d.original_filename || d.title}</span>
              <button onClick={() => onDownload(d)} data-testid={`zone-${cat}-download-${d.id}`}
                className="text-muted-foreground hover:text-foreground" title={downloadCopy}>
                <Download className="w-3.5 h-3.5" />
              </button>
              {d.verification_status !== "verified" && (
                <button onClick={() => onRemove(d)} data-testid={`zone-${cat}-remove-${d.id}`}
                  className="text-muted-foreground hover:text-red-400" title={removeCopy}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-auto pt-5">
        <input
          ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
          data-testid={`zone-${cat}-input`}
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }}
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid={`zone-${cat}-btn`}
          className={`w-full inline-flex items-center justify-center gap-2 h-10 rounded-full text-[11px] tracking-widest uppercase transition ${
            has ? "border border-border bg-background/40 text-foreground hover:bg-secondary" : "bg-emerald-500 hover:bg-emerald-400 text-white"
          } ${busy ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <Upload className="w-3.5 h-3.5" />
          {busy ? "…" : (has ? replaceCopy : spec.uploadCta)}
        </button>
        <p className="mt-2 text-center text-[10px] tracking-wide text-muted-foreground">{fmt}</p>
      </div>
    </div>
  );
};
